// AuthGroup.tsx
import GxIconButton from "@/app/components/common/actions/buttons/GxIconButton";
import { t } from "i18next";
import "./AuthMatrix.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GxButton from "@/app/components/common/actions/buttons/GxButton";
import GxDialog from "@/app/models/dialogs/GxDialog";
import AppDrawer from "@/app/models/drawer/AppDrawer";
import GxTextBox from "@/app/components/common/actions/inputs-b/GxTextBox";
import {
  gxAuthMatrixGroupCreateApi,
  gxAuthMatrixGroupDeleteApi,
  gxAuthMatrixGroupValidateDelete,
} from "@/app/apiConfig/services/adminMenuAbstractApiLayers";
import { gxsmeuserLookupsApi } from "@/app/apiConfig/services/customerAbstractApiLayer";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { RequestConfirmation } from "@/app/components/common/alert/RequestConfirmation";
import { useNavigate } from "react-router-dom";
import { useAuthGroups } from "@/app/hooks/useAdminApiHooks"; // Import the new hook
import { AuthGroupItemSkeleton } from "../common/skeleton-loader/AdminSkeletons";
import GxCompactSuccess from "@/app/models/dialogs/SuccessModals/GxCompactSuccess";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage"; // NEW: inline validation error

interface AuthGroupItem {
  authGroupId: number;
  groupCode: string;
  groupName: string;
  groupDescription: string;
  groupSmeCompId?: number;
  bankId?: number;
  approvalLevel?: number;
  // True for groups that only exist in local state and have not yet been
  // persisted to the server via the Submit button.
  isNew?: boolean;
}

interface AuthGroupCreatePayloadItem {
  groupCode: string;
  groupName: string;
  groupDescription: string;
}

interface AuthGroupLookupItem {
  gxLookupId: number;
  gxLookupType: string;
  gxLookupCode: string;
  gxLookupValue: string;
  gxLookupDesc: string | null;
  gxLookupSeq: number;
  menuId: number;
  permissionId: number;
}

// ── NEW: shape for the GxSuccessCompact popup state ──
interface SuccessPopupState {
  open: boolean;
  primaryText: string;
  secondaryText: string;
  isError: boolean;
}

// ── NEW: shape for the inline delete-validation error state ──
interface DeleteErrorState {
  errorCode?: string;
  message?: string;
}

const INITIAL_SUCCESS_STATE: SuccessPopupState = {
  open: false,
  primaryText: "",
  secondaryText: "",
  isError: false,
};

// Group code that is never deletable, regardless of persisted/new state.
const NON_DELETABLE_GROUP_CODE = "Z";

// Auto-dismiss duration (ms) for the inline delete-validation error.
const DELETE_ERROR_AUTO_DISMISS_MS = 5000;

// Picks the first lookup code that isn't already used by an existing group
const getNextAvailableGroupCode = (
  groups: AuthGroupItem[],
  availableCodes: string[],
) => {
  const usedCodes = new Set(groups.map((item) => item.groupCode));
  return availableCodes.find((code) => !usedCodes.has(code));
};

const AuthGroup = () => {
  const router = useNavigate();
  const [isOpen, setIsOpen] = useState(true);

  const [authGroups, setAuthGroups] = useState<AuthGroupItem[]>([]);
  const [authGroupCodes, setAuthGroupCodes] = useState<string[]>([]);

  // Drives the skeleton rows in the group list (initial fetch + refetches).
  const [groupsLoading, setGroupsLoading] = useState(true);
  // Drives the full SubmitLoading overlay for discrete actions (delete, submit).
  const [loading, setLoading] = useState(false);

  // Tracks whether any local add/edit/delete has happened since the last
  // successful save, so Submit only calls the API when there's something to persist.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ── NEW: single popup state reused for delete + submit (create/update) ──
  const [successPopup, setSuccessPopup] = useState<SuccessPopupState>(
    INITIAL_SUCCESS_STATE,
  );
  const closeSuccessPopup = () =>
    setSuccessPopup((prev) => ({ ...prev, open: false }));

  // ── NEW: inline error shown when gxAuthMatrixGroupValidateDelete blocks a delete ──
  const [deleteError, setDeleteError] = useState<DeleteErrorState | null>(
    null,
  );
  const deleteErrorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearDeleteErrorTimeout = () => {
    if (deleteErrorTimeoutRef.current) {
      clearTimeout(deleteErrorTimeoutRef.current);
      deleteErrorTimeoutRef.current = null;
    }
  };

  const showDeleteError = (error: DeleteErrorState) => {
    clearDeleteErrorTimeout();
    setDeleteError(error);
    deleteErrorTimeoutRef.current = setTimeout(() => {
      setDeleteError(null);
      deleteErrorTimeoutRef.current = null;
    }, DELETE_ERROR_AUTO_DISMISS_MS);
  };

  // Clean up any pending auto-dismiss timeout on unmount.
  useEffect(() => {
    return () => clearDeleteErrorTimeout();
  }, []);

  // Counter used to mint temporary negative ids for groups added locally
  // (i.e. not yet created on the server).
  const tempIdRef = useRef(0);
  const getTempId = () => {
    tempIdRef.current -= 1;
    return tempIdRef.current;
  };

  // Use the new hook for auth groups
  const authGroupPayload = useMemo(() => ({}), []);

  const {
    data: authGroupsData,
    loading: authGroupsLoading,
    refetch: refetchAuthGroups,
  } = useAuthGroups(authGroupPayload);

  // Drawer state for add/edit group
  const [enableDrawer, setEnableDrawer] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [groupName, setGroupName] = useState("");

  // Flip this on every submit attempt so GxTextBox re-runs its own
  // validation, even if the field was already touched before
  const [validationTrigger, setValidationTrigger] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<AuthGroupItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load auth groups using the hook's data
  const loadAuthGroups = async () => {
    const response = await refetchAuthGroups();
    if (response?.status === 1) {
      setAuthGroups(response.data ?? []);
    }
  };

  const loadAuthGroupCodes = async () => {
    const response = await gxsmeuserLookupsApi(
      JSON.stringify({ gxLookupType: "AUTH_GROUP_CODE" }),
    );
    if (response?.status === 1) {
      const codes = (response.data ?? [])
        .slice()
        .sort(
          (a: AuthGroupLookupItem, b: AuthGroupLookupItem) =>
            a.gxLookupSeq - b.gxLookupSeq,
        )
        .map((lookup: AuthGroupLookupItem) => lookup.gxLookupValue);
      setAuthGroupCodes(codes);
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setGroupsLoading(true);
      try {
        await Promise.all([loadAuthGroups(), loadAuthGroupCodes()]);
      } finally {
        setGroupsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Update authGroups when hook data changes
  useEffect(() => {
    if (authGroupsData?.status === 1) {
      setAuthGroups(authGroupsData.data ?? []);
    }
  }, [authGroupsData]);

  const usedGroupCodes = new Set(authGroups.map((item) => item.groupCode));
  const isAddDisabled =
    authGroupCodes.length > 0 &&
    authGroupCodes.every((code) => usedGroupCodes.has(code));

  const handleClose = () => {
    setIsOpen(false);
    router(-1);
  };

  const closeDrawer = () => {
    setEnableDrawer(false);
    setGroupName("");
    setIsEditMode(false);
    setEditingGroupId(null);
    setValidationTrigger(false);
  };

  const handleAddGroup = () => {
    if (isAddDisabled) return;

    console.log("Add group clicked");
    setIsEditMode(false);
    setEditingGroupId(null);
    setGroupName("");
    setValidationTrigger(false);
    setEnableDrawer(true);
  };

  const handleEditGroup = (item: AuthGroupItem) => {
    console.log(`Edit group: ${item.groupCode}`);
    setIsEditMode(true);
    setEditingGroupId(item.authGroupId);
    setGroupName(item.groupName);
    setValidationTrigger(false);
    setEnableDrawer(true);
  };

  const handleDeleteGroup = (item: AuthGroupItem) => {
    console.log(`Delete group: ${item.groupCode}`);
    // Belt-and-suspenders: the button is already disabled for this case,
    // but guard here too in case handleDeleteGroup is ever invoked another way.
    if (item.groupCode === NON_DELETABLE_GROUP_CODE) return;

    setDeleteTarget(item);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  // Locally-added (unsaved) groups are removed from state only — no delete
  // API call, since they were never persisted to the server. Persisted
  // groups are deletable too: they're first validated via
  // gxAuthMatrixGroupValidateDelete, and only actually removed (via the
  // existing delete API) when that validation returns data === true. Both
  // calls run under the same `loading` flag so the full-screen
  // SubmitLoading overlay covers the whole validate → delete sequence, not
  // just the final delete call. If validation blocks the delete, the
  // returned errorCode is surfaced inline via RequiredMessage instead.
  const confirmDeleteGroup = async () => {
    if (!deleteTarget) return;

    if (deleteTarget.isNew) {
      setAuthGroups((prevGroups) =>
        prevGroups.filter(
          (item) => item.authGroupId !== deleteTarget.authGroupId,
        ),
      );
      closeDeleteConfirm();
      return;
    }

    setLoading(true);
    try {
      // ── NEW: validate whether this persisted group can be deleted
      // before calling the actual delete API. ──
      const validateResponse = await gxAuthMatrixGroupValidateDelete({
        id: deleteTarget.authGroupId,
      });

      if (validateResponse?.data !== true) {
        showDeleteError({
          errorCode: validateResponse?.errorCode,
          message: validateResponse?.errorMessage,
        });
        return;
      }

      const response = await gxAuthMatrixGroupDeleteApi({
        numberList: [deleteTarget.authGroupId],
      });

      if (response?.status === 1) {
        setAuthGroups((prevGroups) =>
          prevGroups.filter(
            (item) => item.authGroupId !== deleteTarget.authGroupId,
          ),
        );
        setHasUnsavedChanges(true);
        // ── NEW: success popup for delete ──
        setSuccessPopup({
          open: true,
          primaryText: "Group Deleted",
          secondaryText: "Successfully",
          isError: false,
        });
      } else {
        // ── NEW: error popup for delete ──
        setSuccessPopup({
          open: true,
          primaryText: "Group Delete",
          secondaryText: "Failed",
          isError: true,
        });
      }
    } catch (err) {
      console.error(err);
      // ── NEW: error popup for delete (network/exception, either the
      // validate call or the delete call) ──
      setSuccessPopup({
        open: true,
        primaryText: "Group Delete",
        secondaryText: "Failed",
        isError: true,
      });
    } finally {
      setLoading(false);
      closeDeleteConfirm();
    }
  };

  // Drawer "save" for a new group: adds it to local state only, marked as
  // unsaved (isNew). No API call happens here — persistence happens on the
  // dialog's Submit button.
  const handleAdd = () => {
    if (!groupName.trim()) {
      setValidationTrigger((prev) => !prev);
      return;
    }

    const nextCode = getNextAvailableGroupCode(authGroups, authGroupCodes);
    if (!nextCode) return;

    const newGroup: AuthGroupItem = {
      authGroupId: getTempId(),
      groupCode: nextCode,
      groupName,
      groupDescription: "",
      isNew: true,
    };

    setAuthGroups((prev) => [...prev, newGroup]);
    setHasUnsavedChanges(true);
    closeDrawer();
  };

  // Drawer "update" for an existing group: updates local state only. No API
  // call happens here — persistence happens on the dialog's Submit button.
  const handleUpdate = () => {
    if (!groupName.trim()) {
      setValidationTrigger((prev) => !prev);
      return;
    }

    setAuthGroups((prev) =>
      prev.map((item) =>
        item.authGroupId === editingGroupId ? { ...item, groupName } : item,
      ),
    );
    setHasUnsavedChanges(true);
    closeDrawer();
  };

  // The dialog's Submit button is the single place that persists local
  // add/edit/delete changes to the server, sending the full current list.
  const handleSubmitGroups = async () => {
    if (!hasUnsavedChanges) {
      handleClose();
      return;
    }

    // ── NEW: same create API is used whether the pending changes are new
    // groups, edits to existing ones, or both — decide the popup wording
    // up front based on whether any local (unsaved) group is new. ──
    const hasNewGroups = authGroups.some((item) => item.isNew);
    const hasExistingGroups = authGroups.some((item) => !item.isNew);
    const successPrimaryText = hasNewGroups
      ? hasExistingGroups
        ? "Group Created"
        : "Group Created"
      : "Group Updated";
    const failurePrimaryText = hasNewGroups
      ? hasExistingGroups
        ? "Group Create "
        : "Group Create"
      : "Group Update";

    const payload: AuthGroupCreatePayloadItem[] = authGroups.map((item) => ({
      groupCode: item.groupCode,
      groupName: item.groupName,
      groupDescription: item.groupDescription,
    }));

    setLoading(true);
    try {
      const response = await gxAuthMatrixGroupCreateApi(payload);
      if (response?.status === 1) {
        setHasUnsavedChanges(false);
        await loadAuthGroups();
        // ── NEW: success popup for create/update ──
        setSuccessPopup({
          open: true,
          primaryText: successPrimaryText,
          secondaryText: "Successfully",
          isError: false,
        });
      } else {
        // ── NEW: error popup for create/update ──
        setSuccessPopup({
          open: true,
          primaryText: failurePrimaryText,
          secondaryText: "Failed",
          isError: true,
        });
      }
    } catch (err) {
      console.error(err);
      // ── NEW: error popup for create/update (network/exception) ──
      setSuccessPopup({
        open: true,
        primaryText: failurePrimaryText,
        secondaryText: "Failed",
        isError: true,
      });
    } finally {
      setLoading(false);
      // handleClose();
    }
  };

  // Drives the skeleton rows: true during the initial fetch and any refetch.
  const isGroupsLoading = groupsLoading || authGroupsLoading;

  // Submit is only meaningful when there's at least one newly-added local
  // group waiting to be persisted — edits/deletes alone don't enable it.
  const hasNewLocalGroup = authGroups.some((item) => item.isNew);

  return (
    <div>
      {loading && <SubmitLoading />}

      <GxDialog
        isOpen={isOpen}
        onClose={handleClose}
        title={t(" Auth")}
        subtitle={t("Group")}
        size="small"
      >
        <div className="gx-auth-group-container">
          {/* ── NEW: inline error when a delete is blocked by validation ── */}
          {deleteError && (
            <RequiredMessage
              message={deleteError.message}
              errorCode={deleteError.errorCode}
            />
          )}

          <div className="gx-auth-group-list">
            {isGroupsLoading ? (
              <AuthGroupItemSkeleton count={3} />
            ) : (
              authGroups.map((group) => {
                // Delete is available for both new (unsaved) and persisted
                // groups — persisted ones just go through the
                // gxAuthMatrixGroupValidateDelete check first. The only
                // group locked out of deletion by default is the reserved
                // "A" group code.
                const isDeleteDisabled =
                  group.groupCode === NON_DELETABLE_GROUP_CODE;

                return (
                  <div key={group.authGroupId} className="gx-auth-group-item">
                    <div className="gx-auth-group-info">
                      <span className="gx-auth-group-name">
                        {group.groupCode}
                      </span>
                      <span className="gx-p14-sb">{group.groupName}</span>
                    </div>
                    <div className="gx-auth-group-item-actions">
                      <GxIconButton
                        type="editv2"
                        size="small"
                        onClick={() => handleEditGroup(group)}
                        alt={`Edit ${group.groupCode}`}
                      />
                      <GxIconButton
                        type="delete"
                        size="small"
                        onClick={() => handleDeleteGroup(group)}
                        alt={`Delete ${group.groupCode}`}
                        disabled={isDeleteDisabled}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="gx-auth-group-actions-container">
            <button
              className="gx-auth-group-actions"
              onClick={handleAddGroup}
              disabled={isAddDisabled}
            >
              <GxIconButton
                type="add"
                size="xsmall"
                onClick={handleAddGroup}
                alt="Add group"
              />
              <span className="gx-p12-md">{t("add_group")}</span>
            </button>
          </div>

          <div className="gx-auth-group-footer">
            <GxButton
              variant="filled"
              bgColor="#000000"
              width="large"
              height="medium"
              onClick={handleSubmitGroups}
              disabled={!hasNewLocalGroup}
            >
              {t("submit")}
            </GxButton>
          </div>
        </div>
      </GxDialog>

      <AppDrawer
        title={isEditMode ? t("edit") : t("add")}
        subtitle={t("group")}
        openDrawer={enableDrawer}
        cancel={t("back")}
        submit={isEditMode ? t("update") : t("save")}
        onClose={closeDrawer}
        onSubmit={isEditMode ? handleUpdate : handleAdd}
        variant="grey"
      >
        <div className="gx-add-edit-amount-card ">
          <h2 className="gx-p14-b">Auth Group name</h2>

          <GxTextBox
            label={t("Group Name")}
            value={groupName}
            onChange={setGroupName}
            required
            triggerValidation={validationTrigger}
            sanitizeVariant={"freeText"}
          />
        </div>
      </AppDrawer>

      {showDeleteConfirm && deleteTarget && (
        <RequestConfirmation
          actionType="delete"
          onClose={closeDeleteConfirm}
          onConfirm={() => confirmDeleteGroup()}
        />
      )}

      {/* ── NEW: single GxSuccessCompact reused for delete + create/update ── */}
      <GxCompactSuccess
        open={successPopup.open}
        close={closeSuccessPopup}
        type="true"
        isError={successPopup.isError}
        primaryText={successPopup?.primaryText}
        secondaryText={successPopup?.secondaryText}
        buttonLabel={t("Ok")}
        onButtonClick={closeSuccessPopup}
      />
    </div>
  );
};

export default AuthGroup;
